/**
 * Mexican Menu Template
 * Vibrant and festive menu design
 */
import React from "react";
import { FTU_LOGO_URL } from "../constants";

export function MexicanTemplate({ 
  menuRef,
  generatedMenu, 
  showPrix,
  customLogoUrl,
  pageOrientation = "portrait"
}) {
  if (!generatedMenu) return null;

  return (
    <div
      ref={menuRef}
      className="relative p-4"
      style={{
        width: pageOrientation === "landscape" ? "297mm" : "210mm",
        minHeight: pageOrientation === "landscape" ? "210mm" : "297mm",
        background: "linear-gradient(135deg, #FF6B35 0%, #F7C548 50%, #00A878 100%)",
      }}
    >
      {/* Mexican decorative pattern */}
      <div className="absolute inset-0 opacity-10" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
      }} />
      
      {/* Decorative border */}
      <div className="absolute inset-3 border-4 border-white/40 rounded-xl" style={{
        borderStyle: 'dashed'
      }} />
      
      {/* Content */}
      <div className="relative z-10 h-full flex flex-col p-6">
        {/* Header with Logo */}
        <div className="text-center mb-4">
          {customLogoUrl ? (
            <img 
              src={customLogoUrl} 
              alt="Logo"
              className="h-20 mx-auto mb-2 drop-shadow-lg"
            />
          ) : (
            <div className="text-5xl mb-2">🌮</div>
          )}
          <h1 className="text-3xl font-bold text-white drop-shadow-lg" style={{
            textShadow: '2px 2px 0 #000, -2px -2px 0 #000, 2px -2px 0 #000, -2px 2px 0 #000'
          }}>
            {generatedMenu.titre || "El Menú"}
          </h1>
          {generatedMenu.sous_titre && (
            <p className="text-yellow-100 text-sm">{generatedMenu.sous_titre}</p>
          )}
        </div>
        
        {/* Menu Grid */}
        <div className="flex-1 grid grid-cols-2 md:grid-cols-3 gap-3 auto-rows-min">
          {generatedMenu.sections?.map((section, sIdx) => (
            <React.Fragment key={sIdx}>
              {/* Section Header */}
              <div className="col-span-2 md:col-span-3 mt-2 first:mt-0">
                <h2 className="text-lg font-bold text-white text-center py-1 bg-red-700/80 rounded-lg backdrop-blur-sm" style={{
                  textShadow: '1px 1px 0 #000'
                }}>
                  {section.type === "entree" ? "🥑 Antojitos" :
                   section.type === "plat" ? "🌮 Platos Fuertes" :
                   section.type === "dessert" ? "🍮 Postres" : "🍹 Bebidas"}
                </h2>
              </div>
              
              {/* Dishes */}
              {section.plats?.map((plat, pIdx) => (
                <div 
                  key={pIdx}
                  className="bg-white/95 rounded-xl overflow-hidden shadow-lg transform hover:scale-105 transition-transform border-2 border-yellow-400"
                >
                  {/* Dish Photo */}
                  <div className="aspect-square bg-gradient-to-br from-yellow-100 to-orange-100 relative overflow-hidden">
                    {plat.image_url ? (
                      <img 
                        src={plat.image_url} 
                        alt={plat.nom}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-4xl">
                        {section.type === "entree" ? "🥑" :
                         section.type === "plat" ? "🌮" :
                         section.type === "dessert" ? "🍮" : "🍹"}
                      </div>
                    )}
                    
                    {/* Price Badge - Papel Picado style */}
                    {showPrix && (
                      <div className={`absolute top-2 right-2 ${plat.original_prix && plat.original_prix !== plat.prix ? 'bg-orange-500' : 'bg-red-600'} text-white font-bold px-3 py-1 rounded text-sm shadow-lg`} style={{
                        clipPath: 'polygon(0 0, 100% 0, 100% 70%, 90% 100%, 80% 70%, 70% 100%, 60% 70%, 50% 100%, 40% 70%, 30% 100%, 20% 70%, 10% 100%, 0 70%)'
                      }}>
                        {plat.original_prix && plat.original_prix !== plat.prix && (
                          <span className="text-xs line-through opacity-60 mr-1">{plat.original_prix.toFixed(0)}€</span>
                        )}
                        {plat.prix.toFixed(0)} €
                      </div>
                    )}
                  </div>
                  
                  {/* Dish Info */}
                  <div className="p-2 text-center bg-gradient-to-r from-green-500 via-white to-red-500">
                    <h3 className="font-bold text-gray-800 text-sm leading-tight">{plat.nom}</h3>
                    {plat.description && (
                      <p className="text-xs text-gray-600 mt-0.5 line-clamp-2">{plat.description}</p>
                    )}
                  </div>
                </div>
              ))}
            </React.Fragment>
          ))}
        </div>
        
        {/* Footer */}
        <div className="text-center mt-4 pt-3 border-t border-white/30">
          {/* Logo Food Truck United */}
          <img 
            src={FTU_LOGO_URL} 
            alt="Food Truck United" 
            className="h-8 mx-auto mb-2 brightness-0 invert opacity-80"
          />
          <p className="text-white/90 text-xs" style={{ textShadow: '1px 1px 0 #000' }}>
            {generatedMenu.food_truck?.nom} • ¡Buen Provecho! 🇲🇽
          </p>
          <p className="text-white/70 text-xs mt-1" style={{ textShadow: '1px 1px 0 #000' }}>
            Pour toutes allergies, merci de nous consulter
          </p>
        </div>
      </div>
    </div>
  );
}

export default MexicanTemplate;
